import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Slider,
  Chip,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  MenuItem,
  TextField,
  Select,
  InputBase,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { VenueFormData } from "../../../../types/venue";
import apiService from "../../../../service/Api";
import { RootState } from "../../../../store/Index";
import { useSelector } from "react-redux";

// ── types ──────────────────────────────────────────────────────────────────

interface ServiceProvider {
  name: string;
  description: string | null;
}

interface ProviderOption {
  value: string;
  label: string;
}

interface PowerProps {
  formData: VenueFormData;
  handleInputChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<VenueFormData>>;
}

const POWER_TYPES = [
  { value: "zesa", label: "Zesa" },
  { value: "generator", label: "Generator" },
  { value: "solar", label: "Solar" },
];

const POWER_OUTAGE_RATES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

// ── styled components (OUTSIDE component to prevent remounting on each render) ──

const FieldLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "textcolor",
})<{ textcolor?: string }>(({ textcolor }) => ({
  fontSize: "13px",
  fontWeight: 500,
  opacity: 0.9,
  color: textcolor,
  marginBottom: "6px",
  lineHeight: 1.4,
}));

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "inputbg" && prop !== "textcolor" && prop !== "placeholdercolor",
})<{ inputbg?: string; textcolor?: string; placeholdercolor?: string }>(
  ({ inputbg, textcolor, placeholdercolor }) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: inputbg,
      borderRadius: "30px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "13px",
      color: textcolor,
      "&::placeholder": {
        color: placeholdercolor,
        opacity: 0.9,
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      fontSize: "13px",
    },
  }),
);

const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== "selectbg",
})<{ selectbg?: string }>(({ selectbg }) => ({
  width: "100%",
  backgroundColor: selectbg,
  borderRadius: "30px",
  fontSize: "13px",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": { padding: "10px 14px" },
}));

// ── component ──────────────────────────────────────────────────────────────

export default function Power({ formData, handleInputChange }: PowerProps) {
  const [backupPowerOptions, setBackupPowerOptions] = useState<
    ProviderOption[]
  >([]);
  const [addBackupPowerOpen, setAddBackupPowerOpen] = useState(false);
  const [newBackupPower, setNewBackupPower] = useState("");
  const [addBackupPowerError, setAddBackupPowerError] = useState<string | null>(
    null,
  );

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color400 = reduxTheme.primary_colors?.[400];
  const color900 = reduxTheme.primary_colors?.[900];

  // ── fetch dynamic backup power options ────────────────────────────────

  const fetchBackupPowerOptions = async () => {
    try {
      const response = await apiService.get<ServiceProvider[]>(
        "/api/configurations/power-backups/",
      );
      setBackupPowerOptions(
        response.data.map((p) => ({ value: p.name, label: p.name })),
      );
    } catch (error) {
      console.error("Failed to fetch backup power options:", error);
      toast.error("Failed to load backup power options.");
    }
  };

  useEffect(() => {
    fetchBackupPowerOptions();
  }, []);

  // ── add new backup power option ──────────────────────────────────────

  const handleAddBackupPower = async () => {
    if (!newBackupPower.trim()) return;
    setAddBackupPowerError(null);
    try {
      await apiService.post("/api/configurations/power-backups/", {
        name: newBackupPower,
      });
      await fetchBackupPowerOptions();
      setNewBackupPower("");
      setAddBackupPowerOpen(false);
      toast.success("Backup power option added successfully!");
    } catch (error) {
      const errorData = (error as { response?: { data?: { name?: string[] } } })
        ?.response?.data;
      console.error("Failed to add new backup power:", error);
      setAddBackupPowerError(
        errorData?.name?.[0] ?? "An error occurred. Please try again.",
      );
      toast.error(errorData?.name?.[0] ?? "An error occurred.");
    }
  };

  // ── backup power checkbox helpers ────────────────────────────────────

  const handleBackupChange = (type: string) => {
    const types = formData.powerBackup
      ? formData.powerBackup
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
      : [];
    const newTypes = types.includes(type)
      ? types.filter((t) => t !== type)
      : [...types, type];
    handleInputChange({
      target: { name: "powerBackup", value: newTypes.join(",") },
    });
  };

  const isBackupSelected = (type: string) => {
    const types = formData.powerBackup
      ? formData.powerBackup.split(",").map((t) => t.trim())
      : [];
    return types.includes(type);
  };

  // ── backup capacity helpers ──────────────────────────────────────────

  const getBackupCapacity = (type: string): string => {
    if (!formData.backupPowerCapacity) return "";
    const entry = formData.backupPowerCapacity
      .split(",")
      .find((e) => e.startsWith(`${type}:`));
    return entry ? (entry.split(":")[1] ?? "") : "";
  };

  const setBackupCapacity = (type: string, value: string) => {
    const entries = formData.backupPowerCapacity
      ? formData.backupPowerCapacity.split(",").filter(Boolean)
      : [];
    const existing = entries.findIndex((e) => e.startsWith(`${type}:`));
    if (existing !== -1) {
      entries[existing] = `${type}:${value}`;
    } else {
      entries.push(`${type}:${value}`);
    }
    handleInputChange({
      target: { name: "backupPowerCapacity", value: entries.join(",") },
    });
  };

  // ── socket type helpers ──────────────────────────────────────────────

  const handleSocketTypeToggle = (type: string) => {
    const types = formData.powerSocketType
      ? formData.powerSocketType
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
      : [];
    const newTypes = types.includes(type)
      ? types.filter((t) => t !== type)
      : [...types, type];
    handleInputChange({
      target: { name: "powerSocketType", value: newTypes.join(",") },
    });
  };

  const isSocketTypeSelected = (type: string) => {
    const types = formData.powerSocketType
      ? formData.powerSocketType.split(",").map((t) => t.trim())
      : [];
    return types.includes(type);
  };

  // ── slider ───────────────────────────────────────────────────────────

  const handleSliderChange = (_: Event, value: number | number[]) => {
    handleInputChange({
      target: {
        name: "power_distance_from_livestream_desk",
        value: String(value),
      },
    });
  };

  const distanceValue =
    parseInt(formData.power_distance_from_livestream_desk || "5") || 5;

  // ── render ───────────────────────────────────────────────────────────

  return (
    <Box>
      {/* ── Row 1: Power Type | Power Outage Rate ── */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color100}>Power Type</FieldLabel>
          <FormControl fullWidth>
            <StyledSelect
              name="powerType"
              value={formData.powerType || ""}
              displayEmpty
              selectbg={color800}
              onChange={(e) =>
                handleInputChange({
                  target: { name: "powerType", value: String(e.target.value) },
                })
              }
              renderValue={(selected) =>
                selected ? (
                  <span style={{ color: color700 }}>{selected as string}</span>
                ) : (
                  <span style={{ color: color300 }}>Select type...</span>
                )
              }
            >
              {POWER_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color100}>Power Outage Rate</FieldLabel>
          <FormControl fullWidth>
            <StyledSelect
              name="powerOutageRate"
              value={formData.powerOutageRate || ""}
              displayEmpty
              selectbg={color800}
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: "powerOutageRate",
                    value: String(e.target.value),
                  },
                })
              }
              renderValue={(selected) =>
                selected ? (
                  <span style={{ color: color700 }}>{selected as string}</span>
                ) : (
                  <span style={{ color: color300 }}>Select outage rate...</span>
                )
              }
            >
              {POWER_OUTAGE_RATES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Grid>

        {/* intentional empty third column to match 3-col grid */}
        <Grid size={{ xs: 12, md: 4 }} />
      </Grid>

      {/* ── Back Up Power ── */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <FieldLabel textcolor={color100} sx={{ mb: 0 }}>
            Back Up Power
          </FieldLabel>
          <IconButton size="small" onClick={() => setAddBackupPowerOpen(true)}>
            <AddCircleOutlineOutlined fontSize="small" />
          </IconButton>
        </Box>
        <Grid container spacing={2}>
          {backupPowerOptions.map((option) => (
            <Grid key={option.value} size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: color800,
                  borderRadius: "50px",
                  paddingRight: "4px",
                  paddingLeft: "4px",
                  height: "36px",
                }}
              >
                <Checkbox
                  checked={isBackupSelected(option.value)}
                  onChange={() => handleBackupChange(option.value)}
                  sx={{
                    padding: "4px",
                    height: "20px",
                    weight: "20px",
                    color: color300,
                    "&.Mui-checked": { color: color400 },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: color100,
                    fontWeight: 400,
                    flexGrow: 1,
                  }}
                >
                  {option.label}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: color900,
                    borderRadius: "50px",
                    border: 1,
                    borderColor: "divider",
                    paddingX: "8px",
                    paddingY: "2px",
                    marginLeft: "8px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <InputBase
                    value={getBackupCapacity(option.value)}
                    onChange={(e) =>
                      setBackupCapacity(option.value, e.target.value)
                    }
                    disabled={!isBackupSelected(option.value)}
                    endAdornment={
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "11px", color: color300, ml: "2px" }}
                      >
                        kw
                      </Typography>
                    }
                    sx={{
                      fontSize: "13px",
                      color: color700,
                      width: "38px",
                      "& input": { padding: 0, textAlign: "center" },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ── Power Socket Type ── */}
      <Box sx={{ mt: 4 }}>
        <FieldLabel textcolor={color100}>Power Socket Type</FieldLabel>
        <Box sx={{ display: "flex", gap: 2 }}>
          {["Round", "Square"].map((type) => (
            <Chip
              key={type}
              label={type}
              clickable
              onClick={() => handleSocketTypeToggle(type)}
              sx={{
                fontSize: "13px",
                height: "38px",
                px: 2,
                borderRadius: "20px",
                backgroundColor: isSocketTypeSelected(type)
                  ? color400
                  : color800,
                color: isSocketTypeSelected(type) ? color900 : color700,
                "&:hover": {
                  backgroundColor: isSocketTypeSelected(type)
                    ? color400
                    : color800,
                  opacity: 0.85,
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* ── Distance from Livedesk ── */}
      <Box sx={{ mb: 4, mt: 2 }}>
        <FieldLabel textcolor={color100}>Distance from Livedesk</FieldLabel>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Slider
              value={Math.min(distanceValue, 100)}
              onChange={handleSliderChange}
              min={0}
              max={100}
              sx={{
                color: color800,
                "& .MuiSlider-thumb": {
                  backgroundColor: color400,
                  width: 15,
                  height: 15,
                },
              }}
            />
          </Box>

          <TextField
            value={formData.power_distance_from_livestream_desk ?? "5"}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;
              if (raw === "" || /^\d+$/.test(raw)) {
                handleInputChange({
                  target: {
                    name: "power_distance_from_livestream_desk",
                    value: raw,
                  },
                });
              }
            }}
            size="small"
            inputProps={{
              inputMode: "numeric",
              style: {
                textAlign: "center",
                fontSize: "13px",
                color: color700,
                padding: "6px 8px",
                width: "48px",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    fontSize="12px"
                    sx={{ color: color300, whiteSpace: "nowrap" }}
                  >
                    m
                  </Typography>
                </InputAdornment>
              ),
              sx: {
                backgroundColor: color800,
                borderRadius: "30px",
                "& fieldset": { border: "none" },
                "&:hover fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none" },
              },
            }}
          />
        </Box>
      </Box>

      {/* ── Add Backup Power Dialog ── */}
      <Dialog
        open={addBackupPowerOpen}
        onClose={() => setAddBackupPowerOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add New Backup Power Option</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FieldLabel textcolor={color100}>Backup Power Option</FieldLabel>
            <StyledTextField
              placeholder="Enter backup power name"
              value={newBackupPower}
              onChange={(e) => setNewBackupPower(e.target.value)}
              required
              error={!!addBackupPowerError}
              helperText={addBackupPowerError}
              FormHelperTextProps={{ sx: { color: "error.light", ml: 0 } }}
              inputbg={color800}
              textcolor={color700}
              placeholdercolor={color300}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddBackupPowerOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddBackupPower}
            variant="contained"
            disabled={!newBackupPower.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
